import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errMsg, inr, dt } from "../../lib/api";
import { Card, PageTitle, Stat, StatusBadge, Table, Notice } from "../../components/ui-kit";

export const inp = "w-full rounded-xl glass px-3 py-2 text-sm text-white focus:border-blue-500 focus:outline-none";
export const btn = "rounded-full bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-2 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50";
export const btnO = "rounded-full glass px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/5";

export function askReason(msg = "Reason for this action (required):") {
  const r = window.prompt(msg);
  if (r === null) return null;
  if (!r.trim()) { toast.error("Reason is required"); return null; }
  return r.trim();
}

export function useLoad(path) {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(path).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [path]);
  useEffect(() => { load(); }, [load]);
  return [d, load];
}

export async function act(fn, ok = "Saved") {
  try { const r = await fn(); toast.success(ok); return r?.data ?? true; } catch (e) { toast.error(errMsg(e)); return null; }
}

export function AdminDashboard() {
  const [d] = useLoad("/admin/dashboard");
  if (!d) return null;
  const { actual: a, pending: p, forecast: f, reconciliation: r } = d;
  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <PageTitle title="Dashboard" sub="All figures come from live database records. Actual, pending and forecast values are separated." />
      <div><p className="mb-3 text-xs font-bold uppercase tracking-wider text-emerald-700">Actual</p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="Registered partners" value={a.registered_partners} testid="m-registered" /><Stat label="Active partners" value={a.active_partners} tone="green" testid="m-active" />
          <Stat label="Partners with plan" value={a.partners_with_plan} /><Stat label="Active surveys" value={a.active_surveys} tone="blue" testid="m-active-surveys" />
          <Stat label="Completed responses" value={a.completed_responses} /><Stat label="Approved rewards" value={inr(a.approved_rewards)} tone="green" />
          <Stat label="Client revenue received" value={inr(a.client_revenue_received)} tone="blue" /><Stat label="Plan payments (test)" value={inr(a.plan_payments_received)} tag="test" />
          <Stat label="Refunds" value={inr(a.refunds)} tone="red" /><Stat label="Successful payouts" value={`${a.successful_payouts} • ${inr(a.paid_out_amount)}`} tone="green" />
          <Stat label="Failed payouts" value={a.failed_payouts} tone="red" /><Stat label="TDS withheld" value={inr(a.tds_withheld)} />
        </div></div>
      <div><p className="mb-3 text-xs font-bold uppercase tracking-wider text-amber-700">Pending</p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="Pending rewards" value={inr(p.pending_rewards)} tone="amber" /><Stat label="Responses to review" value={p.pending_reviews} tone="amber" testid="m-pending-reviews" />
          <Stat label="Withdrawal requests" value={p.withdrawal_requests} tone="amber" /><Stat label="KYC pending" value={p.kyc_pending} tone="amber" />
          <Stat label="Open tickets" value={p.open_tickets} /><Stat label="Commissions accrued (pending)" value={inr(p.commissions_accrued_pending)} />
          <Stat label="Fraud alerts" value={p.fraud_alerts} tone="red" /><Stat label="Pending payments" value={p.pending_payments} />
        </div></div>
      <div className="grid gap-6 md:grid-cols-2">
        <Card><p className="text-xs font-bold uppercase tracking-wider text-purple-400">Forecast / committed</p><div className="mt-3 space-y-1 text-sm"><p>Campaign reward budgets: <b>{inr(f.campaign_reward_budgets)}</b></p><p>Budget committed: <b>{inr(f.budget_committed)}</b></p></div></Card>
        <Card data-testid="reconciliation-card"><p className="text-xs font-bold uppercase tracking-wider text-slate-500">Payment reconciliation</p><div className="mt-3 space-y-1 text-sm"><p>Paid orders: <b>{r.paid_orders}</b> • Activations: <b>{r.paid_orders_with_activation}</b></p><p>Webhook events: <b>{r.webhook_events}</b> • Unprocessed: <b>{r.unprocessed_webhooks}</b></p><StatusBadge status={r.paid_orders <= r.paid_orders_with_activation && !r.unprocessed_webhooks ? "ok" : "review"} /></div></Card>
      </div>
    </div>
  );
}

export function Health() {
  const [d] = useLoad("/admin/health");
  return (
    <div data-testid="admin-health"><PageTitle title="Integration health" sub="Honest status of every external integration." />
      <div className="grid gap-3 md:grid-cols-2">{(d || []).map((h) => <Card key={h.name} className="!p-4"><div className="flex items-center justify-between"><p className="font-semibold text-white">{h.name}</p><StatusBadge status={h.status} /></div><p className="mt-1 text-xs text-slate-500">{h.note}</p></Card>)}</div>
    </div>
  );
}

export function AuditLogs() {
  const [d] = useLoad("/admin/audit-logs");
  const [mail] = useLoad("/admin/emails");
  return (
    <div className="space-y-6" data-testid="admin-audit"><PageTitle title="Security & audit logs" />
      <Table rows={d} cols={[{ k: "created_at", l: "Time", r: (r) => dt(r.created_at) }, { k: "actor_email", l: "Actor" }, { k: "actor_role", l: "Role" }, { k: "action", l: "Action" }, { k: "entity", l: "Entity", r: (r) => `${r.entity} ${r.entity_id ? r.entity_id.slice(0, 8) : ""}` }, { k: "reason", l: "Reason" }, { k: "ip", l: "IP" }]} />
      <h2 className="text-lg font-bold text-white">Email outbox <span className="text-xs font-semibold text-purple-600">(MOCK — not sent)</span></h2>
      <Table rows={mail} cols={[{ k: "created_at", l: "Time", r: (r) => dt(r.created_at) }, { k: "to", l: "To" }, { k: "subject", l: "Subject" }, { k: "status", l: "Status", r: (r) => <StatusBadge status="mock" /> }]} />
    </div>
  );
}

function Field({ k, v, onChange }) {
  if (typeof v === "boolean") return <label className="flex items-center justify-between gap-3 rounded-xl border p-3 text-sm"><span className="capitalize">{k.replace(/_/g, " ")}</span><input type="checkbox" checked={v} onChange={(e) => onChange(e.target.checked)} data-testid={`set-${k}`} /></label>;
  if (Array.isArray(v)) return <label className="block text-xs font-semibold capitalize text-slate-500">{k.replace(/_/g, " ")} (comma separated)<input className={inp} value={v.join(",")} onChange={(e) => onChange(e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} data-testid={`set-${k}`} /></label>;
  return <label className="block text-xs font-semibold capitalize text-slate-500">{k.replace(/_/g, " ")}<input className={inp} type={typeof v === "number" ? "number" : "text"} value={v ?? ""} onChange={(e) => onChange(typeof v === "number" ? Number(e.target.value) : e.target.value)} data-testid={`set-${k}`} /></label>;
}

export function SettingsForm({ k, title, note, readonly = [] }) {
  const [d, load] = useLoad(`/admin/settings/${k}`);
  const [v, setV] = useState(null);
  useEffect(() => { if (d) setV(d); }, [d]);
  if (!v) return null;
  const save = async () => {
    const reason = askReason();
    if (!reason || !window.confirm("Confirm this sensitive setting change? It will be audit-logged.")) return;
    const r = await act(() => api.put(`/admin/settings/${k}`, { value: v, reason, confirm: true }));
    if (r) load();
  };
  return (
    <Card data-testid={`settings-${k}`}>
      <p className="font-bold text-white">{title}</p>
      {note && <div className="mt-2"><Notice tone="purple">{note}</Notice></div>}
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {Object.entries(v).filter(([key, val]) => typeof val !== "object" || Array.isArray(val)).map(([key, val]) => readonly.includes(key) ? <p key={key} className="rounded-xl bg-white/5 p-3 text-xs"><b className="capitalize">{key.replace(/_/g, " ")}:</b> {String(val)}</p> : <Field key={key} k={key} v={val} onChange={(nv) => setV({ ...v, [key]: nv })} />)}
      </div>
      {Array.isArray(v.levels) && (
        <div className="mt-4 grid gap-3 sm:grid-cols-3">{v.levels.map((l, i) => (
          <div key={l.level} className="rounded-xl border p-3 text-sm"><p className="font-semibold">Level {l.level}</p>
            <input type="number" className={`${inp} mt-2`} value={l.rate} onChange={(e) => { const lv = [...v.levels]; lv[i] = { ...l, rate: Number(e.target.value) }; setV({ ...v, levels: lv }); }} data-testid={`level-${l.level}-rate`} />
            <label className="mt-2 flex items-center gap-2 text-xs"><input type="checkbox" checked={l.enabled} onChange={(e) => { const lv = [...v.levels]; lv[i] = { ...l, enabled: e.target.checked }; setV({ ...v, levels: lv }); }} data-testid={`level-${l.level}-enabled`} />Enabled</label></div>
        ))}</div>
      )}
      <button onClick={save} className={`${btn} mt-4`} data-testid={`save-settings-${k}`}>Save changes</button>
    </Card>
  );
}
